import { useMemo } from 'react'
import { MRT_ColumnDef } from 'material-react-table'
import Clear from '@mui/icons-material/Clear'
import Warning from '@mui/icons-material/Warning'
import {
  Box,
  Chip,
  Container,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query'
import { useGetDocumentWorkflowStates } from '@/service-library/hooks/document-workflow-states'
import { useCancelEvents } from '@/service-library/hooks/events'
import { useGetProjects } from '@/service-library/hooks/projects'
import { useGetWorkflowStates } from '@/service-library/hooks/workflow-states'
import { DocumentWorkflowState } from '@/types/document-workflow-states'
import PaginatedResponse from '@/types/paginated-response'
import { getIdleTime, prettifyDate } from '@/utils/time'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import useZerapixTable from '@/components/zerapix-table/useZerapixTable'
import ZerapixTable from '@/components/zerapix-table/ZerapixTable'

type InFlightDocRow = DocumentWorkflowState & {
  project_id?: string
  project_name?: string
  workflow_state_name?: string
  isStale: boolean
}

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000

type ClearButtonProps = {
  documentId: string
  listQueryKey: QueryKey
}

const ClearButton = ({ documentId, listQueryKey }: ClearButtonProps) => {
  const queryClient = useQueryClient()
  const { cancelEventsAsync } = useCancelEvents({
    onMutate: ({ id }) => {
      queryClient.setQueryData<
        InfiniteData<PaginatedResponse<DocumentWorkflowState>>
      >(listQueryKey, (oldData) => {
        if (!oldData) return oldData
        const newResults = oldData.pages.map((page) => {
          return {
            ...page,
            results: page.results.filter((dws) => dws.document_id !== id),
          }
        })
        return {
          ...oldData,
          pages: newResults,
        }
      })
    },
  })

  const handleClear = async () => {
    // First, cancel all events for the document
    await cancelEventsAsync({ id: documentId })
  }

  return (
    <Tooltip title="Clear Events & Reset Processing">
      <IconButton
        onClick={(e) => {
          e.stopPropagation()
          handleClear()
        }}
        size="small"
        color="error"
      >
        <Clear fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

export default function InFlightDocsPage() {
  // Fetched so we can display the name in the table
  const { projects = [] } = useGetProjects({
    filters: {
      limit: '1000',
      fields__only: 'id,name',
    },
  })

  // Fetched so we can display the name in the table
  const { workflowStates = [] } = useGetWorkflowStates({
    filters: {
      limit: '1000',
      fields__only: 'id,name',
    },
  })

  const projectMap = useMemo(() => {
    return new Map(projects.map((p) => [p.id, p.name]))
  }, [projects])

  const workflowStateMap = useMemo(() => {
    return new Map(workflowStates.map((ws) => [ws.id, ws.name]))
  }, [workflowStates])

  const {
    documentWorkflowStates = [],
    isPending,
    queryKey,
  } = useGetDocumentWorkflowStates({
    filters: {
      limit: '1000',
      processing_started_at__isnull: 'false',
      ordering: '-processing_started_at',
      fields__only:
        'id,document_id,processing_started_at,entered_at,workflow_state_id,status,short_description,document',
      document__fields__only: 'id,name,project_id',
    },
    refetchInterval: 30000,
  })

  const rows: InFlightDocRow[] = useMemo(() => {
    const now = Date.now()
    return documentWorkflowStates.map((state) => {
      const processingStartedAt = state.processing_started_at
        ? new Date(state.processing_started_at).getTime()
        : 0
      const isStale = now - processingStartedAt > FIFTEEN_MINUTES_MS

      return {
        ...state,
        project_id: state.document?.project_id,
        project_name: state.document?.project_id
          ? projectMap.get(state.document.project_id)
          : undefined,
        workflow_state_name: state.workflow_state_id
          ? workflowStateMap.get(state.workflow_state_id)
          : undefined,
        isStale,
      }
    })
  }, [documentWorkflowStates, projectMap, workflowStateMap])

  const columns: MRT_ColumnDef<InFlightDocRow>[] = useMemo(
    () => [
      {
        header: 'Status',
        accessorKey: 'isStale',
        size: 100,
        Cell: ({ row }) =>
          row.original.isStale ? (
            <Chip
              icon={<Warning />}
              label="Stale"
              color="error"
              size="small"
              variant="filled"
            />
          ) : (
            <Chip
              label="Active"
              color="success"
              size="small"
              variant="outlined"
            />
          ),
      },
      {
        header: 'Document Name',
        accessorFn: (row) => row.document?.name || 'N/A',
        size: 200,
      },
      {
        header: 'Project',
        accessorFn: (row) => row.project_name || row.project_id || 'N/A',
        size: 200,
      },
      {
        header: 'Workflow State',
        accessorFn: (row) =>
          row.workflow_state_name || row.workflow_state_id || 'N/A',
        size: 200,
      },
      {
        header: 'Run Time',
        accessorFn: (row) => row.processing_started_at,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string
          return value ? (
            <Tooltip
              title={`Processing Started: ${prettifyDate(value)}`}
              enterDelay={500}
            >
              <Typography variant="body2">{getIdleTime(value)}</Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2">N/A</Typography>
          )
        },
        size: 150,
      },
      {
        header: 'Processing Started',
        accessorFn: (row) =>
          row.processing_started_at
            ? prettifyDate(row.processing_started_at)
            : 'N/A',
        size: 180,
      },
      {
        header: 'State Duration',
        accessorFn: (row) => row.entered_at,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string
          return (
            <Tooltip
              title={`Entered Workflow State: ${prettifyDate(value)}`}
              enterDelay={500}
            >
              <Typography variant="body2">{getIdleTime(value)}</Typography>
            </Tooltip>
          )
        },
        size: 150,
      },
      {
        header: 'Entered At',
        accessorFn: (row) => prettifyDate(row.entered_at),
        size: 180,
      },
    ],
    [],
  )

  const table = useZerapixTable<InFlightDocRow>({
    data: rows,
    columns,
    enableColumnResizing: true,
    enableStickyHeader: true,
    enableTopToolbar: false,
    enableTableFooter: false,
    enablePagination: false,
    enableRowActions: true,
    getRowId: (row) => row.id,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 80,
      },
    },
    renderRowActions: ({ row }) => {
      return (
        <Stack direction="row" spacing={0.5}>
          <CopyIDButton
            asIconButton
            stringToCopy={row.original.document_id}
            size="small"
          />
          <ClearButton
            documentId={row.original.document_id}
            listQueryKey={queryKey}
          />
        </Stack>
      )
    },
    state: {
      showSkeletons: isPending,
      density: 'compact',
    },
    muiTableHeadCellProps: {
      sx: {
        pt: 1,
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        height: 'calc(100vh - 216px)',
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 216px)',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        cursor: 'pointer',
      },
      onClick: () => {
        const { project_id, document_id } = row.original
        if (project_id && document_id) {
          window.open(
            `/projects/${project_id}/documents?document_id=${document_id}`,
            '_blank',
          )
        }
      },
    }),
  })

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">In-Flight Documents</Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          Total: {rows.length}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Stale: {rows.filter((r) => r.isStale).length}
        </Typography>
      </Box>
      <ZerapixTable<InFlightDocRow> table={table} />
    </Container>
  )
}
